import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type AudienceOption,
  type BuyOption,
  type CguConfig,
  type CumulOption,
  type PerOrderOption,
  type ShippingOption,
  type SubOption,
  type UsageOption,
} from '@/lib/utils/coupon-utils';
import { ArrowLeft } from 'lucide-react';

import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Textarea } from '../ui/textarea';
import { BaseImportViewProps } from './import-view-props';

interface CGURowProps {
  label: React.ReactNode | null;
  include: boolean;
  onIncludeChange: (v: boolean) => void;
  value: string;
  onValueChange: (v: string) => void;
  options: { value: string; label: string }[];
}

interface CGURowRequiredProps {
  label: React.ReactNode;
  value: string;
  onValueChange: (v: string) => void;
  options: { value: string; label: string }[];
}

const CGURow: React.FC<CGURowProps> = ({
  label,
  include,
  onIncludeChange,
  value,
  onValueChange,
  options,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
      <Checkbox
        checked={include}
        onCheckedChange={(ch) => onIncludeChange(Boolean(ch))}
        className="self-center"
      />
      <div className="flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:items-center">
        {label}
        <Select value={value} onValueChange={onValueChange}>
          <SelectTrigger className="h-8 w-full sm:w-48">
            <SelectValue
              placeholder={t(
                'components.import_flow.coupons_cgu.choose_placeholder'
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

const CGURowRequired: React.FC<CGURowRequiredProps> = ({
  label,
  value,
  onValueChange,
  options,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 text-sm sm:flex-row sm:flex-wrap sm:items-center">
      {label}
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className="h-8 w-full sm:w-48">
          <SelectValue
            placeholder={t(
              'components.import_flow.coupons_cgu.choose_placeholder'
            )}
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

interface ViewCouponsCguProps extends BaseImportViewProps {
  cgu: CguConfig;
  setCgu: (cgu: CguConfig | ((prev: CguConfig) => CguConfig)) => void;
  buildCouponTerms: () => string[];
  setCouponTermsTemplate: (template: string[]) => void;
}

export const ViewCouponsCgu: React.FC<ViewCouponsCguProps> = ({
  setImportView,
  cgu,
  setCgu,
  buildCouponTerms,
  setCouponTermsTemplate,
}) => {
  const { t } = useTranslation();

  // Memoize form validation to avoid recalculating on every render
  const isFormInvalid = useMemo(() => {
    return !(
      !!cgu.audience.option &&
      !!cgu.usage.option &&
      (!cgu.buy.include || !!cgu.buy.option) &&
      (!cgu.perOrder.include || !!cgu.perOrder.option) &&
      !!cgu.cumul.option &&
      (!cgu.subscription.include || !!cgu.subscription.option) &&
      (!cgu.shipping.include || !!cgu.shipping.option)
    );
  }, [cgu]);

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md p-1 hover:bg-muted"
            onClick={() => setImportView('coupons')}
            aria-label={t('components.import_flow.coupons_cgu.back_to_coupons')}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <DialogTitle>
            {t('components.import_flow.coupons_cgu.title')}
          </DialogTitle>
        </div>
        <DialogDescription>
          {t('components.import_flow.coupons_cgu.description')}
        </DialogDescription>
      </DialogHeader>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-1 space-y-4 overflow-y-auto rounded-lg bg-muted/20 p-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <CGURowRequired
              label={
                <span>
                  {t('components.import_flow.coupons_cgu.audience.label')}
                </span>
              }
              value={cgu.audience.option ?? ''}
              onValueChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  audience: { ...p.audience, option: v as AudienceOption },
                }))
              }
              options={[
                {
                  value: 'all',
                  label: t(
                    'components.import_flow.coupons_cgu.audience.all_clients'
                  ),
                },
                {
                  value: 'new',
                  label: t(
                    'components.import_flow.coupons_cgu.audience.new_clients_only'
                  ),
                },
              ]}
            />
            <span className="text-sm">.</span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <CGURowRequired
              label={
                <span>
                  {t('components.import_flow.coupons_cgu.usage.label')}
                </span>
              }
              value={cgu.usage.option ?? ''}
              onValueChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  usage: { ...p.usage, option: v as UsageOption },
                }))
              }
              options={[
                {
                  value: 'one',
                  label: t('components.import_flow.coupons_cgu.usage.once'),
                },
                {
                  value: 'many',
                  label: t('components.import_flow.coupons_cgu.usage.multiple'),
                },
              ]}
            />
            <span className="text-sm">
              {t('components.import_flow.coupons_cgu.usage.per_client')}.
            </span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <CGURow
              label={
                <span>{t('components.import_flow.coupons_cgu.buy.label')}</span>
              }
              include={cgu.buy.include}
              onIncludeChange={(v) =>
                setCgu((p) => ({ ...p, buy: { ...p.buy, include: v } }))
              }
              value={cgu.buy.option ?? ''}
              onValueChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  buy: { ...p.buy, option: v as BuyOption },
                }))
              }
              options={[
                {
                  value: 'buy_many',
                  label: t(
                    'components.import_flow.coupons_cgu.buy.multiple_codes'
                  ),
                },
                {
                  value: 'buy_one',
                  label: t(
                    'components.import_flow.coupons_cgu.buy.single_code'
                  ),
                },
              ]}
            />
            <span className="text-sm">.</span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <CGURow
              label={null}
              include={cgu.perOrder.include}
              onIncludeChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  perOrder: { ...p.perOrder, include: v },
                }))
              }
              value={cgu.perOrder.option ?? ''}
              onValueChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  perOrder: { ...p.perOrder, option: v as PerOrderOption },
                }))
              }
              options={[
                {
                  value: 'one_per_order',
                  label: t(
                    'components.import_flow.coupons_cgu.per_order.one_per_order'
                  ),
                },
                {
                  value: 'many_per_order',
                  label: t(
                    'components.import_flow.coupons_cgu.per_order.many_per_order'
                  ),
                },
              ]}
            />
            <span className="text-sm">
              {t('components.import_flow.coupons_cgu.per_order.on_brand_site')}.
            </span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <CGURowRequired
              label={
                <span>
                  {t('components.import_flow.coupons_cgu.cumul.label')}
                </span>
              }
              value={cgu.cumul.option ?? ''}
              onValueChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  cumul: { ...p.cumul, option: v as CumulOption },
                }))
              }
              options={[
                {
                  value: 'non_cumulable',
                  label: t(
                    'components.import_flow.coupons_cgu.cumul.non_cumulable'
                  ),
                },
                {
                  value: 'cumulable',
                  label: t(
                    'components.import_flow.coupons_cgu.cumul.cumulable'
                  ),
                },
              ]}
            />
            <span className="text-sm">
              {t('components.import_flow.coupons_cgu.cumul.with_other_offers')}.
            </span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <CGURow
              label={
                <span>
                  {t('components.import_flow.coupons_cgu.subscription.label')}
                </span>
              }
              include={cgu.subscription.include}
              onIncludeChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  subscription: { ...p.subscription, include: v },
                }))
              }
              value={cgu.subscription.option ?? ''}
              onValueChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  subscription: { ...p.subscription, option: v as SubOption },
                }))
              }
              options={[
                {
                  value: 'no_subscription',
                  label: t(
                    'components.import_flow.coupons_cgu.subscription.no_subscription'
                  ),
                },
                {
                  value: 'subscription',
                  label: t(
                    'components.import_flow.coupons_cgu.subscription.with_subscription'
                  ),
                },
              ]}
            />
            <span className="text-sm">
              {t(
                'components.import_flow.coupons_cgu.subscription.on_brand_site'
              )}
              .
            </span>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
            <CGURow
              label={null}
              include={cgu.shipping.include}
              onIncludeChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  shipping: { ...p.shipping, include: v },
                }))
              }
              value={cgu.shipping.option ?? ''}
              onValueChange={(v) =>
                setCgu((p) => ({
                  ...p,
                  shipping: { ...p.shipping, option: v as ShippingOption },
                }))
              }
              options={[
                {
                  value: 'shipping_paid',
                  label: t(
                    'components.import_flow.coupons_cgu.shipping.shipping_paid'
                  ),
                },
                {
                  value: 'shipping_free',
                  label: t(
                    'components.import_flow.coupons_cgu.shipping.shipping_free'
                  ),
                },
              ]}
            />
            <span className="text-sm">
              {t('components.import_flow.coupons_cgu.shipping.on_brand_site')}.
            </span>
          </div>
          <div className="pt-2">
            <label className="text-sm font-medium">
              {t('components.import_flow.coupons_cgu.extra_info.label')}
            </label>
            <Textarea
              placeholder={t(
                'components.import_flow.coupons_cgu.extra_info.placeholder'
              )}
              rows={3}
              value={cgu.extra}
              onChange={(e) => setCgu((p) => ({ ...p, extra: e.target.value }))}
            />
          </div>
        </div>
        <div className="flex flex-col gap-2 border-t bg-background p-4 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => setImportView('coupons')}
            className="w-full sm:w-auto"
          >
            {t('general.previous')}
          </Button>
          <Button
            disabled={isFormInvalid}
            onClick={() => {
              const lines = buildCouponTerms();
              setCouponTermsTemplate(lines);
              setImportView('instructionsSelect');
            }}
            className="w-full sm:w-auto"
          >
            {t('general.next')}
          </Button>
        </div>
      </div>
    </>
  );
};
